import React, { useContext, useEffect, useRef } from "react";
import Navbar from "../components/Navbar/Navbar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LoginModal from "../common/LoginModal/LoginModal";
import Sidebar from "../components/Sidebar/Sidebar";
import SkipLink from "../components/SkipLink/SkipLink";
import usePageBottom from "../hooks/usePageBottom";
import useRouteFocus from "../hooks/useRouteFocus";
import AuthContext from "../store/auth-context";
import ForgotPasswordModal from "../common/ForgotPasswordModal/ForgotPasswordModal";
import PasswordUpdatedModal from "../common/PasswordUpdatedModal/PasswordUpdatedModal";
import { useTranslation } from "react-i18next";
import { automationTestIds } from "../testing/automationTestIds";
import { appConfig } from "../configs/tenantConfig";

const RESET_PASSWORD_GUARD_KEY = "reset_password_in_progress";

const BaseLayout = () => {
  const authContext = useContext(AuthContext);
  const customElementRef = useRef(null);
  const reachedBottom = usePageBottom(
    { rootMargin: 0, threshold: 0.5 },
    customElementRef,
  );
  const location = useLocation();
  const { t } = useTranslation();
  const mainContentRef = useRef(null);
  useRouteFocus(mainContentRef);

  const isPublicPage =
    location.pathname.includes("/organisations") ||
    location.pathname.includes("/users/update-password") ||
    location.pathname.includes("/users/reset-password") ||
    location.pathname.includes("/credentials") ||
    location.pathname.includes("/credentials/verify");

  const isLandingPage =
    appConfig.showLandingPage &&
    location.pathname === "/" &&
    !authContext.isLoggedIn;

  const isResetPasswordPage = location.pathname.includes(
    "/users/reset-password",
  );

  const isCredentialPreviewPage = location.pathname.includes(
    "/credentials/verify",
  );

  const isWalletOfferPage =
    location.pathname.includes("/credentials/add-to-wallet") ||
    location.pathname.startsWith("/wallet-offer/");

  const isActivateAccountPage = location.pathname.includes("/users/activate");

  const shouldShowSidebar =
    !isLandingPage &&
    !isResetPasswordPage &&
    !isCredentialPreviewPage &&
    !isWalletOfferPage &&
    !isActivateAccountPage &&
    (authContext.isLoggedIn || !isPublicPage);
  const shouldShowNavbar =
    !isLandingPage &&
    !isResetPasswordPage &&
    !isCredentialPreviewPage &&
    !isWalletOfferPage &&
    (authContext.isLoggedIn || !isPublicPage);

  const navigate = useNavigate();
  const TriggerLoginModuleBtnFromModule = () => {
    authContext.setTriggerLoginModuleToggleValue(false);
  };
  const TriggerLoginModuleBtn = () => {
    authContext.setTriggerLoginModuleToggleValue(true);
  };

  useEffect(() => {
    authContext.hasDiscoveryReachedBottomHandler(reachedBottom);
  }, [reachedBottom]);

  const storedRedirect = localStorage.getItem("redirectAfterLogin");

  useEffect(() => {
    const isResetPasswordFlowActive =
      localStorage.getItem(RESET_PASSWORD_GUARD_KEY) === "true";
    const isOnResetPasswordRoute = location.pathname.includes(
      "/users/reset-password",
    );

    if (!isResetPasswordFlowActive || isOnResetPasswordRoute) {
      return;
    }

    if (authContext.isLoading) {
      return;
    }

    if (authContext.isLoggedIn) {
      authContext.logout();
      return;
    }

    if (!authContext.isLoggedIn) {
      localStorage.removeItem(RESET_PASSWORD_GUARD_KEY);
    }
  }, [
    authContext.isLoading,
    authContext.isLoggedIn,
    authContext.logout,
    location.pathname,
  ]);

  useEffect(() => {
    const authToken = localStorage.getItem("auth_token");
    const isAuthStateLoaded = authContext.isLoggedIn !== undefined;

    if (!isAuthStateLoaded) {
      return;
    }

    if (
      authContext.isLoggedIn &&
      storedRedirect &&
      !location.pathname.includes("/users/reset-password")
    ) {
      localStorage.removeItem("redirectAfterLogin");
      navigate(storedRedirect, { replace: true });
    } else if (
      !authContext.isLoggedIn &&
      storedRedirect &&
      !authToken &&
      !isLandingPage &&
      !isWalletOfferPage
    ) {
      authContext.chooseSignUp_SignIn(true);
      TriggerLoginModuleBtn();
    }
  }, [authContext, storedRedirect, navigate, isLandingPage, isWalletOfferPage]);

  return (
    <React.Fragment>
      <SkipLink />
      {shouldShowNavbar && (
        <div
          id={automationTestIds.layoutNavbarWrapper}
          className="fixed top-0 w-full z-10"
        >
          <Navbar />
        </div>
      )}
      <div
        className={
          isLandingPage ? "h-screen overflow-hidden" : "overflow-y-hidden"
        }
      >
        {shouldShowSidebar && (
          <aside
            id={automationTestIds.layoutSidebarAside}
            className="fixed top-[5.1rem] left-0 w-fit h-[calc(100vh-5.1rem)] z-10"
            aria-label="Sidebar"
          >
            <div className="h-full overflow-hidden">
              <Sidebar />
            </div>
          </aside>
        )}
        <div
          id={automationTestIds.layoutBaseMain}
          className={`${isLandingPage ? "" : "transition-all"} ${
            shouldShowSidebar
              ? "mb-[4rem] sm:!mb-[0rem] ml-0 sm:!ml-[5.7rem]"
              : "ml-0"
          } ${shouldShowNavbar ? "mt-[5.1rem]" : ""} ${
            isLandingPage ? "h-screen w-full overflow-hidden" : "overflow-auto"
          }`}
        >
          <main
            id="main-content"
            ref={mainContentRef}
            tabIndex={-1}
            className={`outline-none focus-visible:outline-none ${
              isLandingPage ? "min-h-screen" : ""
            }`}
          >
            <Outlet />
          </main>
        </div>
      </div>
      <LoginModal
        TriggerBtn={
          isLandingPage ? false : authContext.triggerLoginModuleToggleValue
        }
        TriggerModuleBtnFromModule={TriggerLoginModuleBtnFromModule}
        DisplayText=""
      />
      <ForgotPasswordModal
        isModalOpen={authContext.isForgotPassword}
        certificate_header={t("authPages:reset_password")}
        certificate_description={t("authPages:reset_password_description")}
      />
      <PasswordUpdatedModal isLandingPage={isLandingPage} />
    </React.Fragment>
  );
};

export default BaseLayout;
